import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ChargeDecoderPage from './pages/ChargeDecoderPage';
import ComparisonPage from './pages/ComparisonPage';
import ChargesIndexPage from './pages/charges/ChargesIndexPage';
import ChargeDetailPage from './pages/charges/ChargeDetailPage';
import { CountryProvider } from './context/CountryContext';
import { ApiKeyProvider } from './context/ApiKeyContext';
import LoadingSpinner from './components/LoadingSpinner';
import FAQPage from './pages/FAQPage';
import AiResponsePage from './pages/AiResponsePage';
import SearchResultsPage from './pages/SearchResultsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

// Restored Pages from flattened structure
import BankChargesPage from './pages/BankChargesPage';
import BankChargeDetailPage from './pages/bank/BankChargeDetailPage';
import UPIChargesPage from './pages/UPIChargesPage';
import UpiChargeDetailPage from './pages/upi/UpiChargeDetailPage';
import CreditCardChargesPage from './pages/CreditCardChargesPage';
import CreditCardDetailPage from './pages/credit-card/CreditCardDetailPage';


// This dedicated component handles all scroll restoration logic.
// It must be rendered within the Router context.
const ScrollRestoration: React.FC = () => {
  const location = useLocation();

  // Set the browser's scroll restoration to manual on initial load.
  useEffect(() => {
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // On every subsequent route change, scroll the window to the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null; // This component does not render any visible UI.
};


// This component contains the main layout.
const AppContent: React.FC = () => {
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      <main className="flex-grow">
        <Routes>
          {/* Main Pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchResultsPage />} />
          <Route path="/ai-decoder" element={<ChargeDecoderPage />} />
          <Route path="/ai-response" element={<AiResponsePage />} />
          <Route path="/compare" element={<ComparisonPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          
          {/* Charge-Centric Pages */}
          <Route path="/charges" element={<ChargesIndexPage />} />
          <Route path="/charges/:slug" element={<ChargeDetailPage />} />

          {/* Bank Pages */}
          <Route path="/bank-charges" element={<BankChargesPage />} />
          <Route path="/bank-charges/:slug" element={<BankChargeDetailPage />} />

          {/* UPI / Payment App Pages */}
          <Route path="/upi-charges" element={<UPIChargesPage />} />
          <Route path="/payment-app-charges" element={<UPIChargesPage />} />
          <Route path="/upi-charges/:slug" element={<UpiChargeDetailPage />} />
          <Route path="/payment-app-charges/:slug" element={<UpiChargeDetailPage />} />
          
          {/* Credit Card Pages */}
          <Route path="/credit-card-charges" element={<CreditCardChargesPage />} />
          <Route path="/credit-card-charges/:slug" element={<CreditCardDetailPage />} />

        </Routes>
      </main>
      <Footer />
    </div>
  );
};

// The main App component sets up all the providers and routing.
const App: React.FC = () => {
  return (
    <CountryProvider>
      <ApiKeyProvider>
        <Router>
          <ScrollRestoration />
          <AppContent />
        </Router>
      </ApiKeyProvider>
    </CountryProvider>
  );
};

export default App;